import { useEffect, useState } from 'react';
import { fetchStoriesList } from '@/features/story/api';

/**
 * Module-level cached total-story-count — drives the AccountRoute
 * "Bibliothek (count)" badge (Batch 8 Commit 5 wiring) and any other
 * surface that needs the unfiltered count without re-fetching the
 * full list.
 *
 * Cache + inflight-promise dedupe pattern mirrors useEntitlement
 * (Batch 7), useSubscriptionState (Batch 9A),
 * useUnreadNotificationCount (Batch 9C).
 *
 * Reads the `total` field of the new envelope `{ items, total }`
 * shape (Batch 8 Commit 0). Fetches with `limit: 1` to minimise
 * payload — total is calculated server-side independently of limit.
 *
 * Invalidation: callers that mutate the user's story set
 * (delete-story in Batch 8 Commit 4) MUST call
 * `clearStoryCountCache()` after the mutation resolves so the next
 * AccountRoute mount sees the updated count.
 */

let cachedCount: number | null = null;
let inflight: Promise<number> | null = null;

export function clearStoryCountCache(): void {
  cachedCount = null;
  inflight = null;
}

async function fetchTotal(): Promise<number> {
  if (inflight) return inflight;
  inflight = fetchStoriesList({ limit: 1 })
    .then((res) => {
      cachedCount = res.total;
      return res.total;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export interface UseStoryCountResult {
  count: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useStoryCount(): UseStoryCountResult {
  const [count, setCount] = useState<number>(cachedCount ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(cachedCount === null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchTotal()
      .then((total) => {
        if (cancelled) return;
        setCount(total);
        setIsLoading(false);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setIsLoading(false);
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const total = await fetchTotal();
      setCount(total);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  return { count, isLoading, error, refetch };
}
